import { architectureUnderFireScenario } from './architectureUnderFire';
import {
  egoCrisisScenario,
  type Character,
  type ChatContent,
  type ChatMessage as ScenarioChatMessage,
  type EgoCrisisScenario as LoadedScenario,
  type SequenceItem,
} from './storyLoader';
import { resolveStoryCharacterAvatar } from '../storyCharacters';
import { getRuntimeScenario } from './scenarioStore';

export const MISSION_TO_STORY = {
  1: 'ego-crisis',
  102: 'architecture-under-fire',
  201: 'architecture-under-fire',
} as const;

export type StoryKey = (typeof MISSION_TO_STORY)[keyof typeof MISSION_TO_STORY];

const SCENARIO_BY_KEY: Record<StoryKey, LoadedScenario> = {
  'ego-crisis': egoCrisisScenario,
  'architecture-under-fire': architectureUnderFireScenario,
};

const STORY_KEY_FROM_STORY: Record<StoryKey, string> = {
  'ego-crisis': 'egocrisis',
  'architecture-under-fire': 'architecture_under_fire',
};

export const egoCrisisScenarioExport = egoCrisisScenario;
export const architectureUnderFireScenarioExport = architectureUnderFireScenario;

export function getScenarioByMissionId(missionId: number): LoadedScenario | undefined {
  // Priorité au scénario chargé depuis le BLOB (store runtime) : les chats, ids
  // et personnages doivent venir de la même source que ChapterDetail/SceneReader.
  const rt = getRuntimeScenario(missionId);
  if (rt) {
    return {
      id: rt.id,
      title: rt.title,
      subtitle: rt.subtitle ?? '',
      certification: rt.certification,
      coverImage: undefined as any,
      characters: rt.characters as unknown as Character[],
      chapters: rt.chapters,
    };
  }
  const key = MISSION_TO_STORY[missionId as keyof typeof MISSION_TO_STORY];
  return key ? SCENARIO_BY_KEY[key] : undefined;
}

function getStoryKeyForMission(missionId: number): string | undefined {
  const key = MISSION_TO_STORY[missionId as keyof typeof MISSION_TO_STORY];
  return key ? STORY_KEY_FROM_STORY[key] : undefined;
}

function findChatItem(scenario: LoadedScenario | undefined, itemId: string): SequenceItem | undefined {
  if (!scenario) return undefined;
  for (const chapter of scenario.chapters) {
    for (const item of chapter.sequence) {
      if (item.type === 'chat' && item.id === itemId) return item;
    }
  }
  return undefined;
}

function resolveCharacterImage(
  storyKey: string | undefined,
  character: Character | undefined,
): any | undefined {
  if (!character) return undefined;
  // Avatar bundlé local (par storyKey), sinon photo blob du personnage (avatarUrl).
  const local = storyKey
    ? resolveStoryCharacterAvatar(storyKey, { id: character.id, name: character.name })
    : undefined;
  if (local) return local;
  const url = (character as any).avatarUrl;
  return url ? { uri: url } : undefined;
}

function characterInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

export interface ScenarioChat {
  missionId: number;
  missionTitle: string;
  itemId: string;
  tutorName: string;
  tutorRole: string;
  tutorAvatar: string;
  tutorColor?: string;
  tutorImage?: any;
  messages: Array<{
    id: string;
    sender: 'tutor';
    text: string;
    timestamp: string;
    type?: ScenarioChatMessage['type'];
    options?: string[];
    correctAnswer?: string | string[];
    explanation?: string;
    characterName?: string;
    characterImage?: any;
  }>;
}

/** Load a chat sequence item from the JSON-sourced scenario for a mission. */
export function getScenarioChat(missionId: number, itemId: string | undefined): ScenarioChat | undefined {
  if (!itemId) return undefined;

  const scenario = getScenarioByMissionId(missionId);
  if (!scenario) return undefined;

  const chat = findChatItem(scenario, String(itemId));
  if (!chat) return undefined;

  const content = chat.content as ChatContent;
  if (!Array.isArray(content?.messages) || content.messages.length === 0) return undefined;

  const storyKey = getStoryKeyForMission(missionId);
  const findCharacter = (characterId: string | undefined): Character | undefined =>
    characterId ? scenario.characters.find(c => c.id === characterId) : undefined;

  const tutor = findCharacter(content.tutorCharacterId);
  const firstSpeaker =
    findCharacter(content.messages.find(m => m.characterId)?.characterId) ?? tutor;
  const headerCharacter = firstSpeaker ?? tutor;
  const headerName = headerCharacter?.name ?? 'Tuteur';
  const tutorImage = resolveCharacterImage(storyKey, headerCharacter);

  const messages: ScenarioChat['messages'] = content.messages.map((m, idx) => {
    const speaker = findCharacter(m.characterId) ?? tutor;
    return {
      id: m.id || `${itemId}-m${idx + 1}`,
      sender: 'tutor',
      text: m.text,
      timestamp: m.timestamp ?? '',
      type: m.type ?? 'text',
      options: m.options,
      correctAnswer: m.correctAnswer,
      explanation: m.explanation,
      characterName: speaker?.name,
      characterImage: resolveCharacterImage(storyKey, speaker),
    };
  });

  return {
    missionId,
    missionTitle: chat.title || scenario.title || 'Chat',
    itemId: String(itemId),
    tutorName: headerName,
    tutorRole: headerCharacter?.role ?? tutor?.role ?? '',
    tutorAvatar: characterInitials(headerName),
    tutorColor: headerCharacter?.color ?? tutor?.color,
    tutorImage,
    messages,
  };
}
